import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';
import cx from 'classnames';
import CustomSpan from '../text/CustomSpan';

type TPlacement = 'top-start' | 'top' | 'top-end' | 'bottom-start' | 'bottom' | 'bottom-end';

interface BasicTooltipProps {
  children: ReactNode;
  placement?: TPlacement;
  open?: boolean;
  text: string;
  arrow?: boolean;
  className?: string;
}

function BasicTooltip({
  children,
  placement = 'top-start',
  open = false,
  text,
  arrow = false,
  className = '',
}: BasicTooltipProps) {
  const [openTooltip, setOpenTooltip] = useState<boolean>(open);
  let openTooltipTimer: any = null;

  const onMouseEnter = () => {
    if (text.length > 0) {
      openTooltipTimer = setTimeout(() => {
        setOpenTooltip(true);
      }, 500);
    }
  };

  const onMouseLeave = () => {
    setOpenTooltip(false);
    clearTimeout(openTooltipTimer);
  };

  return (
    <StyledBasicTooltip
      className={className}
      data-component-name="BasicTooltip"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
      <div
        className={cx(`basicTooltip__text_box ${placement}`, {
          open: open || openTooltip,
          close: !open && !openTooltip,
        })}
      >
        <CustomSpan fontColor="#fff">{text}</CustomSpan>
        <div
          className={cx(`basicTooltip__arrow ${placement}`, {
            arrow: arrow,
            none: !arrow,
          })}
        ></div>
      </div>
    </StyledBasicTooltip>
  );
}

export default React.memo(BasicTooltip);

const StyledBasicTooltip = styled.div<{}>`
  position: relative;
  width: 100%;
  height: 100%;
  .basicTooltip__text_box {
    position: absolute;
    z-index: 1500;
    max-width: calc(100% * 2);
    width: max-content;
    padding: 5px;
    border-radius: 5px;
    background-color: #25256f;
    white-space: pre-wrap;
    word-break: normal;
    &.open {
      display: block;
    }
    &.close {
      display: none;
    }

    &.top-start,
    &.top,
    &.top-end {
      top: -8px;
    }

    &.top-start {
      left: 0;
      transform: translate(0%, -100%);
    }
    &.top {
      left: 50%;
      transform: translate(-50%, -100%);
    }
    &.top-end {
      right: 0;
      transform: translate(-50%, -100%);
    }

    &.bottom-start,
    &.bottom,
    &.bottom-end {
      bottom: -8px;
    }

    &.bottom-start {
      left: 0;
      transform: translate(0%, 100%);
    }
    &.bottom {
      left: 50%;
      transform: translate(-50%, 100%);
    }
    &.bottom-end {
      right: 0;
      transform: translate(-50%, 100%);
    }

    .basicTooltip__text {
      color: #fff;
    }
  }

  .basicTooltip__arrow {
    position: absolute;
    width: 0;
    height: 0;
    &.top-start,
    &.top,
    &.top-end {
      bottom: 0;
      border-bottom: 4px solid transparent;
      border-top: 4px solid #25256f;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
    }
    &.top-start {
      left: 0;
      transform: translate(50%, 100%);
    }
    &.top {
      left: 50%;
      transform: translate(-50%, 100%);
    }
    &.top-end {
      right: 0;
      transform: translate(-50%, 100%);
    }

    &.bottom-start,
    &.bottom,
    &.bottom-end {
      top: 0;
      border-bottom: 4px solid #25256f;
      border-top: 4px solid transparent;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
    }

    &.bottom-start {
      left: 0;
      transform: translate(50%, -100%);
    }
    &.bottom {
      left: 50%;
      transform: translate(-50%, -100%);
    }
    &.bottom-end {
      right: 0;
      transform: translate(-50%, -100%);
    }
  }
`;
